import { Badge, Flex, Text } from '@mantine/core';
import { EditableTileContainer } from '@dealstack/frontend/shared/components/EditableTileContainer/EditableTileContainer.tsx';
import React from 'react';
import ReactCountryFlag from 'react-country-flag';
import { COUNTRIES } from '@dealstack/shared/lib/countries/countriesData.ts';
import { GetPersonTaxInformationRes } from '@dealstack/shared/lib/generated';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { TEST_ELEMENT_IDS, makeComplexDataTestId } from '@dealstack/shared/tests/testElementIds';

interface Props {
	taxData: GetPersonTaxInformationRes;
	onRemove: (ba: GetPersonTaxInformationRes) => void;
	onEdit: (ba: GetPersonTaxInformationRes) => void;
	hasEditPermission: boolean;
}

export const TaxInformationTile = ({ taxData, onRemove, onEdit, hasEditPermission }: Props) => {
	const tCommonUi = useTranslationCommonUi();
	const country = COUNTRIES.find((c) => c.country_code === taxData.countryCode);
	const summaryLabel =
		taxData.authority || taxData.number
			? `${taxData.authority || tCommonUi('tin')}:`
			: tCommonUi('tax_residence');

	return (
		<EditableTileContainer
			hasEditPermission={hasEditPermission}
			onEditClick={() => onEdit(taxData)}
			onDeleteClick={() => onRemove(taxData)}
			dataTestId={makeComplexDataTestId(TEST_ELEMENT_IDS.CAP_TAX_INFO_TILE, taxData.number)}
			deleteBtnDataTestId={makeComplexDataTestId(
				TEST_ELEMENT_IDS.EDITABLE_TILE_DELETE_BUTTON,
				taxData.number
			)}
			editBtnDataTestId={makeComplexDataTestId(
				TEST_ELEMENT_IDS.EDITABLE_TILE_EDIT_BUTTON,
				taxData.number
			)}
		>
			<Flex direction="column" gap="0.25rem">
				<Text fw={600} c="dark.8">
					{summaryLabel} {taxData.number}
				</Text>

				{country && (
					<Badge
						color="gray.4"
						variant="outline"
						radius="xl"
						size="sm"
						p="0.25rem 0.5rem"
						leftSection={
							<ReactCountryFlag
								countryCode={country.country_code}
								svg
								style={{
									width: '0.75rem',
									height: '0.75rem',
								}}
								title={country.country_code}
							/>
						}
					>
						<Text c="dark.8" fw={400}>
							{country.name}
						</Text>
					</Badge>
				)}
			</Flex>
		</EditableTileContainer>
	);
};
